import React, {Component} from "react";
import {Form, FormInstance, Input, Radio} from "antd";
import ApplicationUpload from "../application-upload/ApplicationUpload";
import ApplicationIcon from "../application-icon/ApplicationIcon";
import {
    base64ToDataUrl,
    errorMessageOrDefault,
    getBase64FromDataUrl,
    getMediaTypeFromDataUrl
} from "../../services/Helpers";
import AppService from "../../services/AppService";
import {validateName, validateVersion} from "./FormValidators";
import {errorNotification, successNotification} from "../../services/NotificationService";
import PlatformType from "../../types/PlatformType";
import AppEntity from "../../types/AppEntity";
import {FormType} from "../../types/FormType";
import {BUILD_PLATFORMS} from "../../services/Platform";
import styles from "./ApplicationForm.module.css";

interface ApplicationFormState {
    platformType?: PlatformType
}

interface ApplicationFormProps {
    onSubmitStart: () => void
    onSubmitEnd: (closeModal?: boolean) => void
    app?: AppEntity
    type: FormType
}

export default class ApplicationForm extends Component<ApplicationFormProps, ApplicationFormState> {
    formRef = React.createRef<FormInstance>();
    private applicationFile?: File;

    state: ApplicationFormState = {platformType: this.props.app?.platform};

    onFinish = (values: any) => {
        const {name, platform, version, description, icon} = values
        const iconBase64EncodedContent = getBase64FromDataUrl(icon) as string
        const iconMediaType = getMediaTypeFromDataUrl(icon) as string

        const formData = new FormData();
        formData.append('name', name);
        formData.append('platform', platform);
        formData.append('version', version);
        formData.append('description', description);
        formData.append('iconBase64EncodedContent', iconBase64EncodedContent);
        formData.append('iconMediaType', iconMediaType);
        if (this.applicationFile) {
            formData.append('content', this.applicationFile as File)
        }

        this.props.onSubmitStart()
        if (FormType.CREATE === this.props.type) {
            this.createApp(formData);
        } else if (FormType.EDIT === this.props.type) {
            this.updateApp(formData)
        }
    };

    createApp = (formData: FormData) => {
        AppService.createApp(formData).then(() => {
            AppService.loadApps()
            successNotification(`${formData.get('name')} ${formData.get('version')} uploaded`, "It's a draft until you release it to staff.")
            this.formRef.current?.resetFields();
            this.props.onSubmitEnd()
        }).catch((error) => {
                errorNotification(errorMessageOrDefault(error, `${formData.get('name')} couldn't be uploaded. Check the file and try again.`))
                this.props.onSubmitEnd(false)
            }
        )
    }

    updateApp = (formData: FormData) => {
        AppService.updateApp(formData, this.props.app?.id as string).then(() => {
            AppService.loadApps()
            successNotification(`${formData.get('name')} is updated`)
            this.onReset()
            this.props.onSubmitEnd()
        }).catch((error) => {
                errorNotification(errorMessageOrDefault(error, `${formData.get('name')} couldn't be updated. Try again.`))
                this.props.onSubmitEnd(false)
            }
        )
    }

    onReset = () => {
        this.formRef.current?.resetFields();
        this.setState({platformType: this.props.app?.platform});
        this.populateFormFields()
    };

    applicationSelectHandler = (applicationFile?: File): void => {
        this.applicationFile = applicationFile;
        this.formRef.current?.setFieldsValue({appFile: applicationFile ? true : undefined})
        if (applicationFile) {
            this.formRef.current?.validateFields(['appFile']).catch(() => undefined)
        }
    }

    onIconChangeHandler = (iconDataUrl: string) => {
        this.formRef.current?.setFieldsValue({icon: iconDataUrl})
        this.formRef.current?.validateFields(['icon']).catch(() => undefined)
    }

    populateFormFields = () => {
        const {app} = this.props
        if (app) {
            const {name, version, platform, description, iconBase64EncodedContent, iconMediaType} = app
            const icon = base64ToDataUrl(iconBase64EncodedContent, iconMediaType);
            this.formRef.current?.setFieldsValue({name, version, platform, description, icon});
        }
    }

    componentDidMount() {
        if (this.props.type === FormType.EDIT) {
            this.populateFormFields()
        }
    }

    render() {
        const isEdit = this.props.type === FormType.EDIT;
        const icon = isEdit && this.props.app?.iconBase64EncodedContent
            ? base64ToDataUrl(this.props.app.iconBase64EncodedContent, this.props.app.iconMediaType)
            : undefined;

        return (
            <Form ref={this.formRef} layout="vertical" requiredMark="optional" onFinish={this.onFinish}
                  className={styles.form}>
                <div className={styles.identity}>
                    <Form.Item name="icon" label="Icon" rules={[{required: true, message: 'Add an icon'}]}>
                        <ApplicationIcon icon={icon} onIconChange={this.onIconChangeHandler}/>
                    </Form.Item>
                    <div className={styles.identityFields}>
                        <Form.Item name="name" label="Name" required
                                   extra="Letters and spaces, as staff will see it"
                                   rules={[{required: true, message: 'Enter the app name'}, validateName]}>
                            <Input maxLength={32}/>
                        </Form.Item>
                        <Form.Item name="version" label="Version" required
                                   extra="Semantic version, like 2.4.0"
                                   rules={[{required: true, message: 'Enter the version'}, validateVersion]}>
                            <Input placeholder="1.0.0" className="mono"/>
                        </Form.Item>
                    </div>
                </div>

                <Form.Item name="platform" label="Platform" required
                           rules={[{required: true, message: 'Choose the platform this build runs on'}]}>
                    <Radio.Group
                        className={styles.platforms}
                        onChange={event => this.setState({platformType: event.target.value})}
                    >
                        {BUILD_PLATFORMS.map(platform => (
                            <Radio.Button key={platform.type} value={platform.type} className={styles.platform}>
                                <span className={styles.platformIcon} aria-hidden>{platform.icon}</span>
                                <span className={styles.platformName}>{platform.name}</span>
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                </Form.Item>

                <Form.Item name="appFile" label="Build file" required={!isEdit}
                           rules={[{required: !isEdit, message: 'Add the build file'}]}>
                    <ApplicationUpload
                        onSelect={this.applicationSelectHandler}
                        platformType={this.state.platformType}
                        hasExistingFile={isEdit}
                    />
                </Form.Item>

                <Form.Item name="description" label="Description" required
                           rules={[{required: true, message: 'Describe what this app is for'}]}>
                    <Input.TextArea showCount maxLength={500} rows={4}
                                    placeholder="What it does and who should install it"/>
                </Form.Item>
            </Form>
        );
    }
}
